/**
 * Hook for managing UniversalFileViewer state (zoom, rotation, navigation, keyboard)
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import type { ViewerFile, FileCategory } from '../types';
import { getFileCategory } from '../utils';

interface UseFileViewerOptions {
  files: ViewerFile[];
  initialIndex: number;
  enableKeyboard: boolean;
  opened: boolean;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
}

export interface UseFileViewerReturn {
  currentIndex: number;
  currentFile: ViewerFile;
  category: FileCategory;
  zoom: number;
  rotation: number;
  canZoom: boolean;
  canRotate: boolean;
  hasNext: boolean;
  hasPrev: boolean;
  goNext: () => void;
  goPrev: () => void;
  goToIndex: (index: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  rotate: () => void;
}

export function useFileViewer({
  files,
  initialIndex,
  enableKeyboard,
  opened,
  onClose,
  onIndexChange,
}: UseFileViewerOptions): UseFileViewerReturn {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);

  // Reset when opened or initialIndex changes
  useEffect(() => {
    if (opened) {
      setCurrentIndex(initialIndex);
      setZoom(100);
      setRotation(0);
    }
  }, [opened, initialIndex]);

  const currentFile = files[currentIndex] || files[0];
  const category = useMemo(
    () => (currentFile ? getFileCategory(currentFile) : 'unknown'),
    [currentFile]
  );

  const canZoom = category === 'image' || category === 'pdf';
  const canRotate = category === 'image';
  const hasNext = currentIndex < files.length - 1;
  const hasPrev = currentIndex > 0;

  const resetViewState = useCallback(() => {
    setZoom(100);
    setRotation(0);
  }, []);

  const goNext = useCallback(() => {
    if (currentIndex < files.length - 1) {
      const next = currentIndex + 1;
      setCurrentIndex(next);
      resetViewState();
      onIndexChange?.(next);
    }
  }, [currentIndex, files.length, resetViewState, onIndexChange]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      const prev = currentIndex - 1;
      setCurrentIndex(prev);
      resetViewState();
      onIndexChange?.(prev);
    }
  }, [currentIndex, resetViewState, onIndexChange]);

  const goToIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < files.length) {
        setCurrentIndex(index);
        resetViewState();
        onIndexChange?.(index);
      }
    },
    [files.length, resetViewState, onIndexChange]
  );

  const zoomIn = useCallback(() => setZoom((prev) => Math.min(prev + 25, 300)), []);
  const zoomOut = useCallback(() => setZoom((prev) => Math.max(prev - 25, 25)), []);
  const resetZoom = useCallback(() => {
    setZoom(100);
    setRotation(0);
  }, []);
  const rotate = useCallback(() => setRotation((prev) => (prev + 90) % 360), []);

  // Keyboard shortcuts
  useEffect(() => {
    if (!enableKeyboard || !opened) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const tag = target.tagName.toLowerCase();
      // Skip when focus is on inputs, textareas, or media elements
      if (tag === 'input' || tag === 'textarea' || tag === 'video' || tag === 'audio') {
        return;
      }

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goNext();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          goPrev();
          break;
        case '+':
        case '=':
          e.preventDefault();
          zoomIn();
          break;
        case '-':
          e.preventDefault();
          zoomOut();
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          rotate();
          break;
        case '0':
          e.preventDefault();
          resetZoom();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enableKeyboard, opened, onClose, goNext, goPrev, zoomIn, zoomOut, rotate, resetZoom]);

  return {
    currentIndex,
    currentFile,
    category,
    zoom,
    rotation,
    canZoom,
    canRotate,
    hasNext,
    hasPrev,
    goNext,
    goPrev,
    goToIndex,
    zoomIn,
    zoomOut,
    resetZoom,
    rotate,
  };
}
